/**
 * Clicking a cell border to make it the thing a border change applies to.
 *
 * WHAT WAS MISSING. Table options could set the border of a WHOLE table, which is the setting
 * nobody actually wants: a procedure's table has a heavy rule under its header row and hairlines
 * between its data rows. Google lets you point at an edge, see it highlight, and then change that
 * edge. Without the pointing there is no way to say which rule you mean.
 *
 * AN EDGE BELONGS TO THE CELL, in this document as in CSS. Each cell may carry its own top, right,
 * bottom and left; anything it does not carry falls back to the table's, which falls back to the
 * house default. That is three levels and they are the same three CSS already has, so the editor
 * draws them with a declaration rather than a computation -- and the rendition reads the same
 * attributes to draw the same lines.
 *
 * STORED AS "<width>|<colour>|<dash>" rather than as an object, because a body with a nested record
 * on four edges of every cell is a quarter again as large for a document whose cells mostly
 * inherit. An absent attribute is the common case and costs nothing, and the dash is left off
 * entirely when it is solid -- which it almost always is.
 */
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export type Edge = "top" | "right" | "bottom" | "left";

export const EDGE_ATTRIBUTE: Record<Edge, string> = {
    top: "borderTop", right: "borderRight", bottom: "borderBottom", left: "borderLeft",
};

/** How close the pointer has to be to an edge to mean it. Google's is about the same. */
const GRAB = 4;

export const borderKey = new PluginKey("parkourTableBorder");

export type BorderSelection = { pos: number; edge: Edge } | null;

export type Dash = "solid" | "dashed" | "dotted";
export const DASHES: Dash[] = ["solid", "dashed", "dotted"];

export const packBorder = (width: number | null, colour: string | null, dash: Dash = "solid") =>
    (width === null || width === undefined)
        ? null
        : `${width}|${colour || "#000000"}${dash && dash !== "solid" ? `|${dash}` : ""}`;

export function unpackBorder(value: any): { width: number; colour: string; dash: Dash } | null {
    if (!value || typeof value !== "string" || !value.includes("|")) return null;
    const [width, colour, dash] = value.split("|");
    const parsed = parseFloat(width);
    if (!Number.isFinite(parsed)) return null;
    return {
        width: parsed,
        colour: colour || "#000000",
        dash: (DASHES as string[]).includes(dash) ? (dash as Dash) : "solid",
    };
}

const attribute = (name: string, edge: Edge) => ({
    [name]: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute(`data-border-${edge}`),
        renderHTML: (attrs: any) => {
            const border = unpackBorder(attrs[name]);
            if (!border) return {};
            const side = edge[0].toUpperCase() + edge.slice(1);
            return {
                [`data-border-${edge}`]: attrs[name],
                style: `border-${edge}: ${border.width}pt ${border.dash} ${border.colour}`,
                // THE EDGE IS ALSO MARKED AS A CLASS so the collapsed-border rules can raise its
                // painting priority: with border-collapse the heavier of two adjacent borders wins,
                // and two hairlines of different colours is a coin toss.
                class: `parkour-edge${side}`,
            };
        },
    },
});

export const CELL_BORDER_ATTRIBUTES = {
    ...attribute("borderTop", "top"),
    ...attribute("borderRight", "right"),
    ...attribute("borderBottom", "bottom"),
    ...attribute("borderLeft", "left"),
};

/** The cell the pointer is in, as a document position, or null. */
function cellAtPointer(view: any, target: EventTarget | null): { pos: number; dom: HTMLElement } | null {
    const dom = (target as HTMLElement)?.closest?.("td, th") as HTMLElement | null;
    if (!dom || !view.dom.contains(dom)) return null;
    try {
        const inside = view.posAtDOM(dom, 0);
        const $inside = view.state.doc.resolve(inside);
        for (let depth = $inside.depth; depth > 0; depth -= 1) {
            const node = $inside.node(depth);
            if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
                return { pos: $inside.before(depth), dom };
            }
        }
    } catch { /* a position that cannot be resolved is not an edge */ }
    return null;
}

/** Which edge of that cell the pointer is on, if any. */
function edgeAt(dom: HTMLElement, x: number, y: number): Edge | null {
    const rect = dom.getBoundingClientRect();
    // THE VERTICAL EDGES ARE TESTED FIRST, because at a corner both are true and a column rule is
    // the one people mean -- it is also the one that resizes, so the cursor agrees with the drag.
    if (Math.abs(x - rect.left) <= GRAB) return "left";
    if (Math.abs(x - rect.right) <= GRAB) return "right";
    if (Math.abs(y - rect.top) <= GRAB) return "top";
    if (Math.abs(y - rect.bottom) <= GRAB) return "bottom";
    return null;
}

export const TableBorders = Extension.create({
    name: "tableBorders",

    addGlobalAttributes() {
        return [{ types: ["tableCell", "tableHeader"], attributes: CELL_BORDER_ATTRIBUTES }];
    },

    addProseMirrorPlugins() {
        return [new Plugin({
            key: borderKey,

            state: {
                init: () => ({ selected: null as BorderSelection }),
                apply(tr, value, _old, next) {
                    const incoming = tr.getMeta(borderKey);
                    if (incoming !== undefined) return { selected: incoming };
                    if (!tr.docChanged || !value.selected) return value;
                    // MAPPED THROUGH THE EDIT, so typing in another cell does not drop the
                    // selection the person is about to act on.
                    const pos = tr.mapping.map(value.selected.pos, -1);
                    const node = next.doc.nodeAt(pos);
                    const kept = node && (node.type.name === "tableCell" || node.type.name === "tableHeader");
                    return { selected: kept ? { ...value.selected, pos } : null };
                },
            },

            props: {
                decorations(state) {
                    const selected: BorderSelection = borderKey.getState(state)?.selected;
                    if (!selected) return DecorationSet.empty;
                    const node = state.doc.nodeAt(selected.pos);
                    if (!node) return DecorationSet.empty;
                    return DecorationSet.create(state.doc, [
                        Decoration.node(selected.pos, selected.pos + node.nodeSize,
                                        { class: `parkour-border-picked parkour-border-${selected.edge}` }),
                    ]);
                },

                handleDOMEvents: {
                    /**
                     * The cursor, and only the cursor.
                     *
                     * A CLASS ON THE EDITOR rather than a style on the cell: the pointer is over a
                     * border for a few pixels either side of it, which is not a region any element
                     * owns -- and writing a style onto a cell would be a DOM change ProseMirror
                     * would reconcile away.
                     */
                    mousemove(view, event) {
                        const cell = cellAtPointer(view, event.target);
                        const edge = cell && edgeAt(cell.dom, (event as MouseEvent).clientX,
                                                    (event as MouseEvent).clientY);
                        const dom = view.dom as HTMLElement;
                        const wanted = edge === "left" || edge === "right" ? "parkour-cursor-column"
                                     : edge ? "parkour-cursor-row" : null;
                        for (const name of ["parkour-cursor-column", "parkour-cursor-row"]) {
                            dom.classList.toggle(name, name === wanted);
                        }
                        return false;
                    },

                    /**
                     * Clicking an edge picks it; clicking anywhere else lets it go.
                     *
                     * RETURNS FALSE EITHER WAY, so this never swallows a click. A press on a column
                     * border is also the start of a resize drag, and refusing to pass it on would
                     * trade one feature for another.
                     */
                    mousedown(view, event) {
                        const cell = cellAtPointer(view, event.target);
                        const edge = cell && edgeAt(cell.dom, (event as MouseEvent).clientX,
                                                    (event as MouseEvent).clientY);
                        const current: BorderSelection = borderKey.getState(view.state)?.selected;
                        if (edge && cell) {
                            view.dispatch(view.state.tr.setMeta(borderKey, { pos: cell.pos, edge }));
                        } else if (current) {
                            view.dispatch(view.state.tr.setMeta(borderKey, null));
                        }
                        return false;
                    },
                },
            },
        })];
    },
});

export const selectedBorder = (editor: any): BorderSelection =>
    (editor && borderKey.getState(editor.state)?.selected) || null;

export function clearBorderSelection(editor: any) {
    if (editor) editor.view.dispatch(editor.state.tr.setMeta(borderKey, null));
}

/**
 * The border that is currently selected, as width and colour -- falling back the way CSS does.
 *
 * So the panel shows what that edge IS, not what the table says, which is what makes the controls
 * read as being about the thing the person just clicked.
 */
export function selectedBorderValue(editor: any, tableWidth: number | null, tableColour: string | null) {
    const picked = selectedBorder(editor);
    if (!picked) return null;
    const node = editor.state.doc.nodeAt(picked.pos);
    const stored = unpackBorder(node?.attrs?.[EDGE_ATTRIBUTE[picked.edge]]);
    return stored || { width: tableWidth ?? 0.75, colour: tableColour ?? "#9AA0A6",
                       dash: "solid" as Dash };
}

/** Write a border onto the selected edge. */
export function setSelectedBorder(editor: any, width: number | null, colour: string | null,
                                  dash: Dash = "solid") {
    const picked = selectedBorder(editor);
    if (!editor || !picked) return false;
    const node = editor.state.doc.nodeAt(picked.pos);
    if (!node) return false;
    const name = EDGE_ATTRIBUTE[picked.edge];
    editor.view.dispatch(editor.state.tr.setNodeMarkup(picked.pos, undefined, {
        ...node.attrs, [name]: packBorder(width, colour, dash),
    }));
    return true;
}
